import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ArrowRight, Check, Link2, Layers, ShieldCheck, CreditCard, Globe, RefreshCw,
  ListChecks, MessageSquareReply, CalendarCheck, FileClock, BadgeCheck, CheckCheck,
  Lock, Landmark,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Reveal, SectionHeading } from "@/components/common";
import { goToAnchor } from "@/lib/nav";
import Seo from "@/components/Seo";
import {
  PRICING, CURRENCY, SIMPLE_RATE, MILESTONE_RATE, money,
} from "@/data/pricing";

const EX = PRICING.example;

const WORKFLOWS = [
  {
    id: "simple",
    icon: Link2,
    name: "Simple Payment Link",
    rate: SIMPLE_RATE,
    rateNote: "successful domestic-card payment",
    tagline: "One clear payment, no stages.",
    bestFor: "Advances, deposits and single invoices.",
    points: [
      "Professional Stripe checkout page",
      "No separate WorkPay workflow fee",
      "You pay the applicable processing cost",
    ],
    highlight: false,
  },
  {
    id: "milestone",
    icon: Layers,
    name: "Milestone Payments",
    rate: MILESTONE_RATE,
    rateNote: "successful domestic-card payment",
    tagline: "Real projects, paid in stages.",
    bestFor: "Multi-stage work with approvals and revisions.",
    points: [
      "Agreed milestones & progress submissions",
      "Customer approvals & revision requests",
      "Staged Stripe payouts + shared record",
    ],
    highlight: true,
  },
];

const WORKFLOW_VALUE = [
  { icon: ListChecks, title: "Agreed milestones", text: "Break the project into clear, agreed stages before any money moves." },
  { icon: FileClock, title: "Progress submissions", text: "Submit work against each milestone so nothing is ambiguous." },
  { icon: CheckCheck, title: "Customer approvals", text: "Customers approve each stage — clarity for both sides." },
  { icon: MessageSquareReply, title: "Revision requests", text: "Handle change requests in one place, not scattered chats." },
  { icon: CalendarCheck, title: "Staged payout eligibility", text: "Approved milestones become eligible for payout, stage by stage." },
  { icon: BadgeCheck, title: "Shared project record", text: "A single activity trail every party can rely on." },
];

const FAQS = [
  {
    q: "Is a Simple Payment Link free?",
    a: `No. Payment processing still applies — ${SIMPLE_RATE} for a successful UAE domestic-card payment through Stripe. There is no separate WorkPay workflow fee on a Simple Payment Link, but you pay the applicable Stripe payment-processing cost.`,
  },
  {
    q: "Does Milestone Payments cost only 1%?",
    a: `No. The ${PRICING.milestone.workpayPct}% is additional to Stripe payment processing, and ${PRICING.milestone.vatPct}% VAT applies to that WorkPay fee. On a UAE domestic card the effective total is ${MILESTONE_RATE}.`,
  },
  {
    q: "Who pays the fees?",
    a: "The vendor. The customer pays the agreed project amount through Stripe, and applicable fees are deducted from the vendor's payout.",
  },
  {
    q: "Why does the final processing fee sometimes change?",
    a: "International cards and currency conversion each add roughly 1% on top, because they cost more to process through Stripe.",
  },
  {
    q: "How and when do I get paid?",
    a: "Payouts are made to your bank account by Stripe. With Milestone Payments, each approved milestone becomes eligible for a Stripe payout, stage by stage.",
  },
  {
    q: "Does WorkPay process or hold card payments?",
    a: "No. Payments are processed and held by Stripe, a globally trusted payments infrastructure. WorkPay provides the software for managing the project, milestone and approval workflow.",
  },
  {
    q: "Is the checkout secure?",
    a: "Yes. Customers pay on a secure, PCI-compliant Stripe checkout. WorkPay never sees or stores raw card details.",
  },
];

export default function PricingPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const createLink = () => goToAnchor(navigate, pathname, "#early-access");

  return (
    <main className="pt-24" data-testid="pricing-page">
      <Seo path="/pricing" />

      {/* ---------- HERO ---------- */}
      <section data-testid="pricing-hero" className="relative overflow-hidden py-16 sm:py-20">
        <div className="absolute -top-32 -left-24 h-96 w-96 rounded-full bg-[#ecfdf3] blur-3xl opacity-80" />
        <div className="absolute -top-20 right-0 h-96 w-96 rounded-full bg-[#ecfeff] blur-3xl opacity-70" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs tracking-[0.2em] uppercase text-[#16a34a] font-bold">
                  Pricing
                </span>
                <StripeBadge />
              </div>
              <h1 className="font-display text-5xl sm:text-6xl font-extrabold tracking-tight text-[#091630] leading-[1.02]">
                Priced for the work, <span className="text-gradient">not the wait.</span>
              </h1>
              <p className="mt-6 text-lg text-slate-500 max-w-md leading-relaxed">
                Two simple ways to get paid — a clean payment link, or full milestone
                payments for real projects. You always know the cost before you send.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  data-testid="pricing-hero-cta"
                  onClick={createLink}
                  className="bg-[#21c55e] text-[#091630] font-bold py-3.5 px-7 rounded-full hover:bg-[#16a34a] hover:text-white transition-colors flex items-center justify-center gap-2 green-shadow"
                >
                  Create your WorkPay link <ArrowRight className="h-5 w-5" />
                </button>
                <button
                  data-testid="pricing-hero-secondary"
                  onClick={() => navigate("/contact")}
                  className="bg-white text-[#091630] font-semibold py-3.5 px-7 rounded-full border border-slate-200 hover:border-slate-300 transition-colors"
                >
                  Talk to the team
                </button>
              </div>
              <p className="mt-6 text-sm text-slate-400 flex items-center gap-2 max-w-md">
                <ShieldCheck className="h-4 w-4 text-[#16a34a] shrink-0" />
                Payments are processed through Stripe. WorkPay provides the project and
                milestone workflow.
              </p>
            </div>
          </Reveal>

          {/* Rate snapshot */}
          <Reveal delay={0.1}>
            <div
              data-testid="pricing-rate-card"
              className="relative bg-white rounded-[2rem] border border-slate-100 soft-shadow-lg p-7 sm:p-9 space-y-5"
            >
              {WORKFLOWS.map((w) => (
                <div
                  key={w.id}
                  className={`rounded-2xl p-5 ${w.highlight ? "bg-[#091630]" : "bg-[#f7faf8] border border-slate-100"}`}
                >
                  <p className={`text-xs font-bold uppercase tracking-[0.16em] ${w.highlight ? "text-[#21c55e]" : "text-slate-400"}`}>
                    {w.name}
                  </p>
                  <div className="flex items-end gap-2 mt-2">
                    <span className={`font-display text-4xl sm:text-5xl font-extrabold ${w.highlight ? "text-white" : "text-[#091630]"}`}>
                      {w.rate.split(" + ")[0]}
                    </span>
                    <span className={`font-display text-xl font-bold mb-1 ${w.highlight ? "text-slate-300" : "text-slate-400"}`}>
                      + {CURRENCY} {PRICING.processing.fixed}
                    </span>
                  </div>
                  <p className={`text-sm mt-1 ${w.highlight ? "text-slate-300" : "text-slate-500"}`}>
                    per {w.rateNote}
                  </p>
                </div>
              ))}
              <p className="text-xs text-slate-400 leading-relaxed">
                UAE domestic cards. International cards and currency conversion may add
                roughly 1% each. Not free — processing always applies.
              </p>
              <div className="pt-4 border-t border-slate-100 flex items-center justify-between gap-3">
                <span className="text-xs text-slate-400">Secure checkout &amp; payouts by</span>
                <StripeBadge />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- STRIPE TRUST STRIP ---------- */}
      <section data-testid="stripe-trust-strip" className="border-y border-slate-100 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-center gap-x-8 gap-y-3 text-sm text-slate-500">
          <span className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-[#635BFF]" />
            Payments powered by <span className="font-extrabold text-[#635BFF]">Stripe</span>
          </span>
          <span className="hidden sm:block h-4 w-px bg-slate-200" />
          <span className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-[#16a34a]" />
            Secure, PCI-compliant checkout
          </span>
          <span className="hidden sm:block h-4 w-px bg-slate-200" />
          <span className="flex items-center gap-2">
            <Landmark className="h-4 w-4 text-[#091630]" />
            Stripe payouts to your bank
          </span>
        </div>
      </section>

      {/* ---------- TWO CHOICES ---------- */}
      <section data-testid="pricing-workflows" className="relative py-16 sm:py-24 bg-[#f7faf8]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionHeading
              overline="Two ways to get paid"
              title="Pick the flow that fits the job."
              subtitle="Both run on Stripe. One is a single clean payment; the other manages the whole project."
              center
            />
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {WORKFLOWS.map((w, i) => (
              <Reveal key={w.id} delay={i * 0.08}>
                <div
                  data-testid={`workflow-card-${w.id}`}
                  className={`h-full rounded-3xl p-8 ${
                    w.highlight
                      ? "bg-white border-2 border-[#21c55e]/40 soft-shadow-lg"
                      : "bg-white border border-slate-100 soft-shadow"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="h-12 w-12 rounded-2xl bg-[#ecfdf3] flex items-center justify-center">
                      <w.icon className="h-6 w-6 text-[#16a34a]" />
                    </div>
                    {w.highlight && (
                      <span className="rounded-full bg-[#ecfdf3] px-3 py-1 text-xs font-bold text-[#16a34a]">
                        Full project workflow
                      </span>
                    )}
                  </div>
                  <h3 className="font-display text-xl font-bold text-[#091630] mt-5">{w.name}</h3>
                  <p className="text-[15px] text-slate-500 mt-1">{w.tagline}</p>
                  <div className="flex items-end gap-2 mt-5">
                    <span className="font-display text-4xl font-extrabold text-[#091630]">{w.rate}</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-1">per {w.rateNote}</p>
                  <p className="text-sm text-[#091630] font-semibold mt-5">Best for</p>
                  <p className="text-sm text-slate-500">{w.bestFor}</p>
                  <ul className="mt-5 space-y-3 pt-5 border-t border-slate-100">
                    {w.points.map((p) => (
                      <li key={p} className="flex items-start gap-2.5 text-[15px] text-[#091630]">
                        <span className="h-5 w-5 rounded-full bg-[#ecfdf3] flex items-center justify-center shrink-0 mt-0.5">
                          <Check className="h-3 w-3 text-[#16a34a]" />
                        </span>
                        {p}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-6 pt-5 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-400">
                    <span>Checkout &amp; payouts via</span>
                    <StripeBadge />
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* processing applies to both */}
          <Reveal delay={0.1}>
            <div
              data-testid="pricing-processing-note"
              className="mt-8 rounded-3xl bg-white border border-slate-100 soft-shadow p-6 sm:p-7 flex items-start gap-4"
            >
              <div className="h-11 w-11 rounded-xl bg-[#ecfeff] flex items-center justify-center shrink-0">
                <CreditCard className="h-5 w-5 text-[#0891b2]" />
              </div>
              <p className="text-[15px] text-slate-600 leading-relaxed">
                <span className="font-bold text-[#091630]">Payment processing applies to both.</span>{" "}
                Every successful UAE domestic-card payment carries {SIMPLE_RATE} in Stripe processing.
                Milestone Payments add a {PRICING.milestone.workpayPct}% WorkPay workflow fee plus{" "}
                {PRICING.milestone.vatPct}% VAT on that fee — an effective {PRICING.milestone.effectivePct}%
                on top, for a {MILESTONE_RATE} total.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- AED 10,000 EXAMPLE ---------- */}
      <section data-testid="pricing-example" className="relative py-16 sm:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionHeading
              overline="A real example"
              title={`On a ${money(EX.amount)} project.`}
              subtitle="Same project, both workflows. Here is exactly what lands in your account."
              center
            />
          </Reveal>

          <div className="mt-12 grid md:grid-cols-2 gap-6">
            {/* Simple */}
            <Reveal delay={0.05}>
              <div data-testid="example-simple" className="h-full rounded-3xl border border-slate-100 bg-[#f7faf8] p-8">
                <h3 className="font-display text-lg font-bold text-[#091630]">Simple Payment Link</h3>
                <dl className="mt-6 space-y-3 text-[15px]">
                  <Row label="Customer pays" value={money(EX.amount)} strong />
                  <Row label="Processing fee" value={`– ${money(EX.simple.processing)}`} />
                  <div className="pt-3 mt-3 border-t border-slate-200">
                    <Row label="Estimated payout" value={money(EX.simple.payout)} big />
                  </div>
                </dl>
              </div>
            </Reveal>

            {/* Milestone */}
            <Reveal delay={0.12}>
              <div data-testid="example-milestone" className="h-full rounded-3xl border-2 border-[#21c55e]/30 bg-white p-8 soft-shadow">
                <h3 className="font-display text-lg font-bold text-[#091630]">Milestone Payments</h3>
                <dl className="mt-6 space-y-3 text-[15px]">
                  <Row label="Customer pays" value={money(EX.amount)} strong />
                  <Row label="Processing fee" value={`– ${money(EX.milestone.processing)}`} />
                  <Row label={`WorkPay fee (${PRICING.milestone.workpayPct}%)`} value={`– ${money(EX.milestone.workpay)}`} />
                  <Row label={`VAT (${PRICING.milestone.vatPct}% of WorkPay fee)`} value={`– ${money(EX.milestone.vat)}`} />
                  <div className="pt-3 mt-3 border-t border-slate-200">
                    <Row label="Estimated payout" value={money(EX.milestone.payout)} big />
                  </div>
                </dl>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="mt-8 text-center">
              <span
                data-testid="example-takeaway"
                className="inline-flex items-center gap-2 rounded-full bg-[#091630] px-6 py-3 text-white font-semibold"
              >
                <span className="h-2 w-2 rounded-full bg-[#21c55e]" />
                On a {money(EX.amount)} project, the milestone workflow adds just {money(EX.milestoneDelta)}.
              </span>
              <p className="text-xs text-slate-400 mt-4 max-w-xl mx-auto">
                UAE domestic-card example. International cards and currency conversion may add roughly 1%
                each. Payments are processed and paid out by Stripe. Figures are estimates, subject to
                final fee verification.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ---------- WHAT THE EXTRA 1% PAYS FOR ---------- */}
      <section data-testid="pricing-value" className="relative py-16 sm:py-24 bg-[#f7faf8]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionHeading
              overline="What you're paying for"
              title="The extra 1% isn't for the card. It's for the project."
              subtitle="Taking a payment is the easy part. The workflow around it is what gets you paid without the chasing."
              center
            />
          </Reveal>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WORKFLOW_VALUE.map((v, i) => (
              <Reveal key={v.title} delay={(i % 3) * 0.06}>
                <div data-testid={`value-${i}`} className="h-full bg-white rounded-2xl border border-slate-100 soft-shadow p-6">
                  <div className="h-11 w-11 rounded-xl bg-[#ecfdf3] flex items-center justify-center mb-4">
                    <v.icon className="h-5 w-5 text-[#16a34a]" />
                  </div>
                  <h3 className="font-display text-base font-bold text-[#091630] mb-1.5">{v.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------- ADDITIONAL CHARGES ---------- */}
      <section data-testid="pricing-additional" className="relative py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionHeading overline="Full transparency" title="Charges that can apply on top." center />
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 gap-5">
            {PRICING.addOns.map((a) => (
              <Reveal key={a.label} delay={0.05}>
                <div className="h-full rounded-2xl border border-slate-100 bg-white soft-shadow p-6 flex items-start gap-4">
                  <div className="h-11 w-11 rounded-xl bg-[#fff7ed] flex items-center justify-center shrink-0">
                    {a.label === "International card" ? (
                      <Globe className="h-5 w-5 text-[#ea580c]" />
                    ) : (
                      <RefreshCw className="h-5 w-5 text-[#ea580c]" />
                    )}
                  </div>
                  <div>
                    <p className="font-display text-base font-bold text-[#091630]">
                      {a.label} <span className="text-[#ea580c]">+{a.pct}%</span>
                    </p>
                    <p className="text-sm text-slate-500 mt-1">Applies to {a.note}, based on Stripe processing.</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-6 text-sm text-slate-500 text-center max-w-2xl mx-auto leading-relaxed">
              {PRICING.otherCharges} The customer pays the agreed project amount, and applicable fees are
              deducted from the vendor's payout.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ---------- FAQ ---------- */}
      <section data-testid="pricing-faq" className="relative py-16 sm:py-24 bg-[#f7faf8]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionHeading overline="FAQ" title="Straight answers on cost." center />
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-10 space-y-3" data-testid="pricing-faq-accordion">
              {FAQS.map((faq, i) => (
                <AccordionItem
                  key={i}
                  value={`p-item-${i}`}
                  data-testid={`pricing-faq-item-${i}`}
                  className="rounded-2xl border border-slate-100 bg-white px-5 soft-shadow"
                >
                  <AccordionTrigger className="text-left text-[#091630] font-bold hover:no-underline hover:text-[#16a34a] py-5">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-500 text-[15px] leading-relaxed pb-5">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* ---------- FINAL CTA ---------- */}
      <section data-testid="pricing-final-cta" className="relative py-20 sm:py-28 bg-[#091630] overflow-hidden">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-[#21c55e]/15 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-[#06b6d4]/15 blur-3xl" />
        <div className="absolute inset-0 dotted-bg opacity-[0.15]" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
              Start the next project with <span className="text-[#21c55e]">clarity.</span>
            </h2>
            <p className="mt-5 text-lg text-slate-300 max-w-xl mx-auto">
              Send a link your customer trusts, and know exactly what lands in your account.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <button
                data-testid="pricing-final-cta-button"
                onClick={createLink}
                className="bg-[#21c55e] text-[#091630] font-bold py-3.5 px-7 rounded-full hover:bg-white transition-colors flex items-center justify-center gap-2"
              >
                Create your WorkPay link <ArrowRight className="h-5 w-5" />
              </button>
              <button
                data-testid="pricing-final-demo"
                onClick={() => navigate("/contact")}
                className="bg-transparent text-white font-semibold py-3.5 px-7 rounded-full border border-white/25 hover:bg-white/10 transition-colors"
              >
                Talk to the team
              </button>
            </div>
            <div className="mt-8 flex justify-center">
              <StripeBadge dark />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

const Row = ({ label, value, strong, big }) => (
  <div className="flex items-center justify-between gap-4">
    <dt className={`${big ? "text-[#091630] font-bold" : "text-slate-500"}`}>{label}</dt>
    <dd
      className={`${
        big
          ? "font-display text-2xl font-extrabold text-[#16a34a]"
          : strong
          ? "font-semibold text-[#091630]"
          : "text-slate-500"
      }`}
    >
      {value}
    </dd>
  </div>
);

const StripeBadge = ({ dark = false, className = "" }) => (
  <span
    data-testid="stripe-badge"
    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${
      dark ? "bg-white/10 text-white" : "bg-[#635BFF]/10 text-[#635BFF]"
    } ${className}`}
  >
    <Lock className="h-3.5 w-3.5" />
    Powered by <span className="font-extrabold tracking-tight">Stripe</span>
  </span>
);
